import { plural } from "./utils";

/**
 * Время раздела. Интерфейс прототипа считает в «минутах от понедельника
 * 00:00» и в номерах дней от того же понедельника — так проще раскладывать
 * день по часам и сравнивать сроки. Здесь это привязано к настоящим часам:
 * отсчёт — от понедельника текущей недели по местному времени, а сервер
 * отдаёт и принимает обычные даты (`toMin`, `fromMin`, `dateOf`, `dayOfDate`).
 *
 * В прототипе была одна демо-неделя. В жизни в пятницу ставят задачу на
 * понедельник, поэтому выбрать можно пять рабочих дней начиная с сегодняшнего
 * (`DAYS`), а не только остаток этой недели.
 */

export interface DayInfo {
  /** номер дня от понедельника этой недели: 0 — пн, 7 — пн следующей */
  idx: number;
  short: string;
  abbr: string;
  /** «понедельник» */
  name: string;
  /** «в понедельник» */
  on: string;
  /** «на среду» */
  acc: string;
  date: number;
  month: string;
}

const SHORT = ["ПН", "ВТ", "СР", "ЧТ", "ПТ", "СБ", "ВС"];
const ABBR = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];
const NAME = ["понедельник", "вторник", "среда", "четверг", "пятница", "суббота", "воскресенье"];
const ON = ["в понедельник", "во вторник", "в среду", "в четверг", "в пятницу", "в субботу", "в воскресенье"];
const ACC = ["понедельник", "вторник", "среду", "четверг", "пятницу", "субботу", "воскресенье"];
const MONTH = ["января", "февраля", "марта", "апреля", "мая", "июня", "июля", "августа", "сентября", "октября", "ноября", "декабря"];

const DAY_MS = 86_400_000;

function mondayOf(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  return x;
}

/** Понедельник 00:00 этой недели — точка отсчёта всех «минут» и «дней». */
export const ANCHOR = mondayOf(new Date());

export function dayInfo(idx: number): DayInfo {
  const d = new Date(ANCHOR);
  d.setDate(d.getDate() + idx);
  const w = (d.getDay() + 6) % 7;
  return { idx, short: SHORT[w], abbr: ABBR[w], name: NAME[w], on: ON[w], acc: ACC[w], date: d.getDate(), month: MONTH[d.getMonth()] };
}

export const at = (day: number, h: number, m = 0) => day * 1440 + h * 60 + m;

/** Минуты от понедельника до указанного момента. */
export const toMin = (t: string | number | Date) => Math.floor((new Date(t).getTime() - ANCHOR.getTime()) / 60000);
export const fromMin = (min: number) => new Date(ANCHOR.getTime() + min * 60000).toISOString();

/** «Сейчас» в минутах от понедельника. */
export const nowMin = () => toMin(Date.now());

export const dayOf = (t: number) => Math.floor(t / 1440);
export const hourOf = (t: number) => (t - dayOf(t) * 1440) / 60;

/** Номер дня → «2026-09-29» по местному календарю. */
export function dateOf(day: number): string {
  const d = new Date(ANCHOR);
  d.setDate(d.getDate() + day);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** «2026-09-29» → номер дня от понедельника. */
export function dayOfDate(date: string): number {
  const [y, m, d] = date.split("-").map(Number);
  return Math.round((new Date(y, m - 1, d).getTime() - ANCHOR.getTime()) / DAY_MS);
}

/** Сегодня — номер дня. В выходные это 5 или 6. */
export const TODAY = dayOf(nowMin());

/** Пять рабочих дней начиная с сегодняшнего (в выходные — с понедельника). */
export const DAYS: DayInfo[] = (() => {
  const out: DayInfo[] = [];
  for (let i = TODAY; out.length < 5; i++) {
    const info = dayInfo(i);
    if (info.short !== "СБ" && info.short !== "ВС") out.push(info);
  }
  return out;
})();

/** Ближайший рабочий день после этого. */
export function nextWorkday(day: number): number {
  for (let i = day + 1; ; i++) {
    const s = dayInfo(i).short;
    if (s !== "СБ" && s !== "ВС") return i;
  }
}

export function hm(h: number) {
  let hh = Math.floor(h);
  let mm = Math.round((h - hh) * 60);
  if (mm === 60) {
    hh += 1;
    mm = 0;
  }
  return `${hh}:${String(mm).padStart(2, "0")}`;
}

export const timeOf = (t: number) => hm(hourOf(t));

function relDay(day: number, now: number) {
  const d = dayOf(now);
  if (day === d) return "Сегодня";
  if (day === d + 1) return "Завтра";
  if (day === d - 1) return "Вчера";
  return null;
}

/** «Сегодня» / «Завтра» / «Чт 1» */
export function dayName(day: number, now: number) {
  const info = dayInfo(day);
  return relDay(day, now) ?? `${info.abbr} ${info.date}`;
}

/** «сегодня» / «завтра» / «в четверг» — для фраз «начнёт в четверг» */
export function dayOn(day: number, now: number) {
  const rel = relDay(day, now);
  return rel ? rel.toLowerCase() : dayInfo(day).on;
}

/** Короткое «сколько прошло»: только что · 20 мин · 1 ч 10 мин · вчера · пн */
export function ago(t: number, now: number) {
  const diff = now - t;
  if (diff < 1) return "только что";
  if (dayOf(t) === dayOf(now)) {
    if (diff < 60) return `${diff} мин`;
    const h = Math.floor(diff / 60);
    const m = diff % 60;
    return m ? `${h} ч ${m} мин` : `${h} ч`;
  }
  if (dayOf(t) === dayOf(now) - 1) return "вчера";
  const info = dayInfo(dayOf(t));
  return dayOf(now) - dayOf(t) < 7 ? info.abbr.toLowerCase() : `${info.date} ${info.month}`;
}

/** Очень коротко: 20 мин · 2 ч · вчера */
export function agoShort(t: number, now: number) {
  const diff = now - t;
  if (diff < 1) return "сейчас";
  if (dayOf(t) === dayOf(now)) return diff < 60 ? `${diff} мин` : `${Math.floor(diff / 60)} ч`;
  return ago(t, now);
}

export function agoLong(t: number, now: number) {
  const diff = now - t;
  if (diff < 1) return "только что";
  if (dayOf(t) === dayOf(now)) {
    if (diff < 60) return `${diff} ${plural(diff, ["минуту", "минуты", "минут"])} назад`;
    return `сегодня в ${timeOf(t)}`;
  }
  if (dayOf(t) === dayOf(now) - 1) return `вчера в ${timeOf(t)}`;
  return `${dayInfo(dayOf(t)).abbr} в ${timeOf(t)}`;
}

/** «в 11:52», «вчера в 17:30» */
export function whenLabel(t: number, now: number) {
  if (dayOf(t) === dayOf(now)) return `в ${timeOf(t)}`;
  if (dayOf(t) === dayOf(now) - 1) return `вчера в ${timeOf(t)}`;
  return `${dayInfo(dayOf(t)).abbr} в ${timeOf(t)}`;
}
